import {
  ReactElement,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { ContentContainer } from './styles';
import { getOverviewInfo } from './helpers/overview';
import { useFilterContext } from './contexts/FilterContext';
import { useToast } from '~/contexts/newToastContext';
import { useTranslation } from 'react-i18next';
import { RowCardsOverview } from './RowCardsOverview';
import { BigNumbers } from './BigNumbers';
import { Health } from './Health';
import { useHealthDataContext } from './contexts/HealthDataContext';

export function OverviewContent(): ReactElement {
  const { client, selectedOptions } = useFilterContext();
  const { refetch } = useHealthDataContext();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<{ hasWater: boolean, hasHealth: boolean } | undefined>();
  const { toast } = useToast();
  const { t } = useTranslation();
  const prevClientRef = useRef<string | undefined>(undefined);

  async function getInfo(clientId: string): Promise<void> {
    const overviewInfo = await getOverviewInfo(Number(clientId));

    if (overviewInfo) {
      setInfo({
        hasWater: overviewInfo.hasWater,
        hasHealth: overviewInfo.hasHealth,
      });

      if (overviewInfo.hasHealth) {
        refetch(Number(client), selectedOptions);
      }
    } else {
      toast(t('erroTitulo'), t('erroBuscaDadosCliente'), 'error');
    }

    setLoading(false);
  }

  useEffect(() => {
    if (client) {
      setLoading(true);
      setInfo(undefined);
      getInfo(client);
      prevClientRef.current = client;
    }
  }, [client]);

  const clientChanged = client && prevClientRef.current !== client;
  const effectiveLoading = loading || !!clientChanged;

  const hasHealth = useMemo(() => !clientChanged && !!info?.hasHealth, [clientChanged, info?.hasHealth]);

  return (
    <ContentContainer>
      <BigNumbers hasHealth={hasHealth} loading={effectiveLoading} />
      <Health hasHealth={!!info?.hasHealth} loading={loading} />
      <RowCardsOverview hasWater={!!(!clientChanged && info?.hasWater)} loading={effectiveLoading} />
    </ContentContainer>
  );
}
